import { useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { getProfile, logout, resendVerification, updateProfile } from "~/api/auth"
import type { ProfileResponse } from "~/api/auth"
import { HeroPortraitCard } from "~/components/HeroPortraitCard"
import { LoggedInScreenCard } from "~/components/LoggedInScreenCard"
import { PageShell } from "~/components/PageShell"
import { ProfileFeedbackSection } from "~/components/ProfileFeedbackSection"
import { assetUrl } from "~/utils/assetUrl"
import {
  ASK_DYNAMO_THOUGHT_FREQUENCIES,
  type AskDynamoThoughtFrequency,
} from "~/data/askDynamoThoughtFrequencies"
import { CheckCircle, ChevronDown, ChevronRight, Mail, PawPrint, Star } from "lucide-react"

type DogSex = "male" | "female"
type KibbleFrequency = "weekly" | "monthly" | "none"

function verificationLinkRemainingCopy(iso: string | null): string {
  if (!iso) return "Verify your email to secure your account."
  const endMs = new Date(iso).getTime()
  const now = Date.now()
  const dayMs = 86_400_000
  const diffDays = Math.ceil((endMs - now) / dayMs)
  if (diffDays <= 0) return "Your verification link may have expired—send a new one below."
  if (diffDays === 1) return "1 day left on your current verification link."
  return `${diffDays} days left on your current verification link.`
}

export function ProfilePage() {
  const navigate = useNavigate()
  const [profile, setProfile] = useState<ProfileResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [dogName, setDogName] = useState("")
  const [dogSex, setDogSex] = useState<DogSex | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [resendSending, setResendSending] = useState(false)
  const [resendMessage, setResendMessage] = useState<string | null>(null)
  const [resendError, setResendError] = useState<string | null>(null)
  const [kibbleEnabled, setKibbleEnabled] = useState(true)
  const [kibbleFrequency, setKibbleFrequency] = useState<KibbleFrequency>("weekly")
  const [kibbleSaving, setKibbleSaving] = useState(false)
  const [kibbleSaveError, setKibbleSaveError] = useState<string | null>(null)
  const [kibbleSaveMessage, setKibbleSaveMessage] = useState<string | null>(null)
  const [thoughtsEnabled, setThoughtsEnabled] = useState(true)
  const [thoughtFrequency, setThoughtFrequency] = useState<AskDynamoThoughtFrequency>("daily")
  const [thoughtsSaving, setThoughtsSaving] = useState(false)
  const [thoughtsSaveError, setThoughtsSaveError] = useState<string | null>(null)
  const [thoughtsSaveMessage, setThoughtsSaveMessage] = useState<string | null>(null)

  useEffect(() => {
    getProfile()
      .then((data) => {
        setProfile(data)
        const dog = data.dog_profile
        if (dog) {
          setDogName(dog.name)
          setDogSex(dog.sex)
        }
        setKibbleEnabled(data.kibble_enabled)
        setKibbleFrequency(
          data.kibble_frequency === "monthly" || data.kibble_frequency === "none"
            ? data.kibble_frequency
            : "weekly"
        )
        const thoughtPref = data.ask_dynamo_thought_preference
        if (thoughtPref) {
          setThoughtsEnabled(thoughtPref.enabled)
          setThoughtFrequency(thoughtPref.frequency)
        }
      })
      .catch(() => navigate("/login", { replace: true }))
      .finally(() => setLoading(false))
  }, [navigate])

  async function handleSaveDogProfile() {
    if (!profile?.dog_profile || !dogSex) return
    setSaveError(null)
    setSaving(true)
    try {
      const updated = await updateProfile({
        dog_name: dogName.trim(),
        sex: dogSex,
      })
      setProfile(updated)
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Could not save")
    } finally {
      setSaving(false)
    }
  }

  async function handleSaveKibblePreferences() {
    setKibbleSaveError(null)
    setKibbleSaveMessage(null)
    setKibbleSaving(true)
    try {
      const frequency: KibbleFrequency = kibbleEnabled ? kibbleFrequency : "none"
      const updated = await updateProfile({ kibble_frequency: frequency })
      setProfile(updated)
      setKibbleEnabled(updated.kibble_enabled)
      setKibbleFrequency(
        updated.kibble_frequency === "monthly" || updated.kibble_frequency === "none"
          ? updated.kibble_frequency
          : "weekly"
      )
      setKibbleSaveMessage("Email preferences saved.")
    } catch (e) {
      setKibbleSaveError(e instanceof Error ? e.message : "Could not save email preferences")
    } finally {
      setKibbleSaving(false)
    }
  }

  async function handleSaveThoughtPreferences() {
    setThoughtsSaveError(null)
    setThoughtsSaveMessage(null)
    setThoughtsSaving(true)
    try {
      const frequency: AskDynamoThoughtFrequency = thoughtsEnabled ? thoughtFrequency : "off"
      const updated = await updateProfile({ ask_dynamo_thought_frequency: frequency })
      setProfile(updated)
      const thoughtPref = updated.ask_dynamo_thought_preference
      if (thoughtPref) {
        setThoughtsEnabled(thoughtPref.enabled)
        setThoughtFrequency(thoughtPref.frequency)
      }
      setThoughtsSaveMessage("Ask Dynamo Thoughts preferences saved.")
    } catch (e) {
      setThoughtsSaveError(e instanceof Error ? e.message : "Could not save Ask Dynamo Thoughts preferences")
    } finally {
      setThoughtsSaving(false)
    }
  }

  async function handleLogout() {
    await logout()
    navigate("/login", { replace: true })
  }

  async function handleResendVerification() {
    setResendError(null)
    setResendMessage(null)
    setResendSending(true)
    try {
      await resendVerification()
      const data = await getProfile()
      setProfile(data)
      setResendMessage("Verification email sent. Check your inbox.")
    } catch (e) {
      setResendError(e instanceof Error ? e.message : "Could not send email")
    } finally {
      setResendSending(false)
    }
  }

  if (loading) {
    return (
      <PageShell>
        <p className="text-[var(--color-text-secondary)]">Loading...</p>
      </PageShell>
    )
  }

  if (!profile) return null

  const dog = profile.dog_profile
  const displayName = dog ? dogName.trim() || dog.name : dogName.trim() || "Your dog"
  const archetypeName = dog?.archetype_label ?? "Archetype"
  const archetypeIcon = dog?.archetype_icon
  const canSaveDog =
    Boolean(dog) && dogName.trim().length > 0 && dogSex !== null && (dogName.trim() !== dog.name || dogSex !== dog.sex)

  return (
    <PageShell innerClassName="max-w-lg w-full" fixedTabNav>
      <LoggedInScreenCard>
        <div className="flex justify-center">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-full border border-[var(--color-divider)] bg-[var(--color-bg-primary)] px-4 py-2 text-sm font-medium text-[var(--color-text-primary)]"
          >
            <PawPrint size={14} />
            {displayName}
            <ChevronDown size={14} />
          </button>
        </div>

        {dog && (
          <div className="mt-4 flex flex-col items-center">
            {archetypeIcon ? (
              <img
                src={assetUrl(`/images/archetypes/logos/${archetypeIcon}`)}
                alt={archetypeName}
                className="h-16 w-16 object-contain"
              />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-full border border-[var(--color-divider)]">
                <Star size={22} className="text-[var(--color-text-secondary)]" />
              </div>
            )}
            <p className="mt-2 text-center text-3xl font-semibold text-[var(--color-text-primary)]">{displayName}</p>
            <p className="text-center text-lg text-[var(--color-text-secondary)]">{archetypeName}</p>
          </div>
        )}

        <section className="mt-5 rounded-xl border border-dashed border-[var(--color-divider)] bg-[var(--color-bg-primary)]/80 p-4">
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">Account</h2>
          <div className="mt-3 divide-y divide-[var(--color-divider)] border-t border-[var(--color-divider)]">
            <div className="py-3 text-sm">
              <div className="flex items-start justify-between gap-3">
                <span className="shrink-0 text-[var(--color-text-secondary)]">Email</span>
                <div className="min-w-0 flex-1 text-right">
                  <p className="break-all font-medium text-[var(--color-text-primary)]">{profile.user.email}</p>
                  {profile.user.email_verified ? (
                    <p className="mt-2 flex items-center justify-end gap-1.5 text-xs text-[var(--color-text-secondary)]">
                      <CheckCircle className="h-3.5 w-3.5 shrink-0 text-emerald-700/85" aria-hidden />
                      <span>Verified</span>
                    </p>
                  ) : (
                    <div className="mt-3 space-y-2 rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-card)]/50 p-3 text-right">
                      <p className="text-xs leading-relaxed text-[var(--color-text-secondary)]">
                        {verificationLinkRemainingCopy(profile.user.email_verification_link_expires_at)}
                      </p>
                      <div className="flex justify-end">
                        <button
                          type="button"
                          disabled={resendSending}
                          onClick={() => void handleResendVerification()}
                          className="inline-flex items-center justify-center gap-2 rounded-lg border border-[var(--color-divider)] bg-[var(--color-bg-primary)] px-3 py-2 text-xs font-medium text-[var(--color-text-primary)] transition hover:opacity-90 disabled:opacity-50"
                        >
                          <Mail className="h-3.5 w-3.5 shrink-0" aria-hidden />
                          {resendSending ? "Sending…" : "Send verification email"}
                        </button>
                      </div>
                      {resendMessage && (
                        <p className="text-xs text-emerald-800/90" role="status">
                          {resendMessage}
                        </p>
                      )}
                      {resendError && (
                        <p className="text-xs text-red-600" role="alert">
                          {resendError}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <Link
              to="/change-password"
              className="flex items-center justify-between py-3 text-sm text-[var(--color-text-primary)]"
            >
              <span>Change password</span>
              <ChevronRight size={16} />
            </Link>
          </div>
        </section>

        <section
          id="email-preferences"
          className="mt-4 rounded-xl border border-dashed border-[var(--color-divider)] bg-[var(--color-bg-primary)]/80 p-4"
        >
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">Email preferences</h2>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
            Receive tips and perspectives tailored to your dog by email.
          </p>

          <label className="mt-4 flex items-center justify-between gap-3 text-sm">
            <span className="text-[var(--color-text-primary)]">Receive kibbles by email</span>
            <button
              type="button"
              role="switch"
              aria-checked={kibbleEnabled}
              onClick={() => setKibbleEnabled((value) => !value)}
              className={`relative h-7 w-12 rounded-full border transition ${
                kibbleEnabled
                  ? "border-[var(--color-text-primary)] bg-[var(--color-text-primary)]"
                  : "border-[var(--color-divider)] bg-[var(--color-surface-card)]"
              }`}
            >
              <span
                className={`absolute top-0.5 h-5 w-5 rounded-full bg-[var(--color-surface-card)] transition ${
                  kibbleEnabled ? "left-6" : "left-1"
                }`}
              />
            </button>
          </label>

          {kibbleEnabled && (
            <div className="mt-4 space-y-2">
              <p className="text-sm text-[var(--color-text-secondary)]">How often?</p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={() => setKibbleFrequency("weekly")}
                  aria-pressed={kibbleFrequency === "weekly"}
                  className={`rounded-lg border px-4 py-2 text-sm font-medium transition ${
                    kibbleFrequency === "weekly"
                      ? "border-[var(--color-text-primary)] bg-[var(--color-text-primary)] text-[var(--color-surface-card)]"
                      : "border-[var(--color-divider)] bg-[var(--color-bg-primary)] text-[var(--color-text-primary)]"
                  }`}
                >
                  Weekly (every 7 days)
                </button>
                <button
                  type="button"
                  onClick={() => setKibbleFrequency("monthly")}
                  aria-pressed={kibbleFrequency === "monthly"}
                  className={`rounded-lg border px-4 py-2 text-sm font-medium transition ${
                    kibbleFrequency === "monthly"
                      ? "border-[var(--color-text-primary)] bg-[var(--color-text-primary)] text-[var(--color-surface-card)]"
                      : "border-[var(--color-divider)] bg-[var(--color-bg-primary)] text-[var(--color-text-primary)]"
                  }`}
                >
                  Monthly (every 28 days)
                </button>
              </div>
            </div>
          )}

          {kibbleSaveError && (
            <p className="mt-2 text-sm text-red-600" role="alert">
              {kibbleSaveError}
            </p>
          )}
          {kibbleSaveMessage && (
            <p className="mt-2 text-sm text-emerald-800/90" role="status">
              {kibbleSaveMessage}
            </p>
          )}
          <button
            type="button"
            disabled={kibbleSaving}
            onClick={() => void handleSaveKibblePreferences()}
            className="mt-3 w-full rounded-lg bg-[var(--color-text-primary)] px-4 py-2 text-sm font-medium text-[var(--color-surface-card)] disabled:opacity-50"
          >
            {kibbleSaving ? "Saving…" : "Save kibbles preferences"}
          </button>

          <div className="mt-6 border-t border-[var(--color-divider)] pt-6">
            <h3 className="text-base font-semibold text-[var(--color-text-primary)]">Ask Dynamo Thoughts</h3>
            <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
              Get fresh questions and dog thoughts to spark ideas and conversations.
            </p>
            <label className="mt-4 flex items-center justify-between gap-3 text-sm">
              <span className="text-[var(--color-text-primary)]">Receive Ask Dynamo Thoughts</span>
              <button
                type="button"
                role="switch"
                aria-checked={thoughtsEnabled}
                onClick={() => setThoughtsEnabled((value) => !value)}
                className={`relative h-7 w-12 rounded-full border transition ${
                  thoughtsEnabled
                    ? "border-[var(--color-ask-dynamo)] bg-[var(--color-ask-dynamo)]"
                    : "border-[var(--color-divider)] bg-[var(--color-surface-card)]"
                }`}
              >
                <span
                  className={`absolute top-0.5 h-5 w-5 rounded-full bg-[var(--color-surface-card)] transition ${
                    thoughtsEnabled ? "left-6" : "left-1"
                  }`}
                />
              </button>
            </label>
            {thoughtsEnabled && (
              <div className="mt-4 flex flex-col gap-2">
                {ASK_DYNAMO_THOUGHT_FREQUENCIES.filter((f) => f.value !== "off").map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setThoughtFrequency(value)}
                    aria-pressed={thoughtFrequency === value}
                    className={`rounded-lg border px-4 py-2 text-left text-sm font-medium transition ${
                      thoughtFrequency === value
                        ? "border-[var(--color-ask-dynamo)] bg-[var(--color-ask-dynamo)] text-white"
                        : "border-[var(--color-divider)] bg-[var(--color-bg-primary)] text-[var(--color-text-primary)]"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            {thoughtsSaveError && (
              <p className="mt-2 text-sm text-red-600" role="alert">
                {thoughtsSaveError}
              </p>
            )}
            {thoughtsSaveMessage && (
              <p className="mt-2 text-sm text-emerald-800/90" role="status">
                {thoughtsSaveMessage}
              </p>
            )}
            <button
              type="button"
              disabled={thoughtsSaving}
              onClick={() => void handleSaveThoughtPreferences()}
              className="mt-3 w-full rounded-lg bg-[var(--color-ask-dynamo)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {thoughtsSaving ? "Saving…" : "Save Ask Dynamo Thoughts preferences"}
            </button>
          </div>
        </section>

        {dog ? (
          <section className="mt-4 rounded-xl border border-dashed border-[var(--color-divider)] bg-[var(--color-bg-primary)]/80 p-4">
            <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
              About {dogName.trim() || dog.name}
            </h2>
            <label className="mt-3 block text-center text-sm text-[var(--color-text-secondary)]" htmlFor="profile-dog-name">
              Name
            </label>
            <input
              id="profile-dog-name"
              value={dogName}
              onChange={(e) => setDogName(e.target.value)}
              className="mt-1 w-full rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-card)] px-3 py-2 text-center text-[var(--color-text-primary)]"
            />
            <p className="mt-3 text-center text-sm text-[var(--color-text-secondary)]">Sex</p>
            <div className="mt-2 flex justify-center gap-3">
              <button
                type="button"
                onClick={() => setDogSex("male")}
                aria-pressed={dogSex === "male"}
                className={`min-w-28 rounded-lg border px-4 py-2 text-sm font-medium transition ${
                  dogSex === "male"
                    ? "border-[var(--color-text-primary)] bg-[var(--color-text-primary)] text-[var(--color-surface-card)]"
                    : "border-[var(--color-divider)] bg-[var(--color-bg-primary)] text-[var(--color-text-primary)]"
                }`}
              >
                Male
              </button>
              <button
                type="button"
                onClick={() => setDogSex("female")}
                aria-pressed={dogSex === "female"}
                className={`min-w-28 rounded-lg border px-4 py-2 text-sm font-medium transition ${
                  dogSex === "female"
                    ? "border-[var(--color-text-primary)] bg-[var(--color-text-primary)] text-[var(--color-surface-card)]"
                    : "border-[var(--color-divider)] bg-[var(--color-bg-primary)] text-[var(--color-text-primary)]"
                }`}
              >
                Female
              </button>
            </div>
            {saveError && (
              <p className="mt-2 text-center text-sm text-red-600" role="alert">
                {saveError}
              </p>
            )}
            <button
              type="button"
              disabled={!canSaveDog || saving}
              onClick={handleSaveDogProfile}
              className="mt-3 w-full rounded-lg bg-[var(--color-text-primary)] px-4 py-2 text-sm font-medium text-[var(--color-surface-card)] disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </section>
        ) : (
          <p className="mt-4 text-center text-sm text-[var(--color-text-secondary)]">
            No dog profile yet. Complete an assessment to add your dog.
          </p>
        )}

        {profile && dog && (
          <HeroPortraitCard profile={profile} onProfileUpdated={setProfile} />
        )}

        <ProfileFeedbackSection />

        <div className="mt-4 flex flex-col items-center gap-3">
          <Link
            to="/assessment?retake=1"
            className="inline-flex w-full max-w-xs justify-center rounded-full border border-[var(--color-divider)] bg-[var(--color-bg-primary)] px-6 py-3 text-sm font-medium text-[var(--color-text-primary)] shadow-sm"
          >
            Retake assessment
          </Link>
          <Link to="/deeper-insights" className="text-sm text-[var(--color-text-primary)] underline">
            Explore what&apos;s coming →
          </Link>
        </div>

        <button
          type="button"
          onClick={handleLogout}
          className="mt-4 w-full text-center text-sm text-[var(--color-text-secondary)] underline"
        >
          Log out
        </button>

      </LoggedInScreenCard>
    </PageShell>
  )
}
